import React, { useState } from 'react';
import Select from '../ui/Select';
import { formatGold } from '../../utils/goldNotation';
import { filterByRole, ROLE_LABELS, STATUS_LABELS } from '../../utils/transparency';

// Cabeçalho e linhas são grids separados: as colunas precisam de larguras fixas
// (sem `auto`) para ficarem alinhadas.
const AGGREGATE_COLS = 'sm:grid-cols-[minmax(0,1fr)_5rem_7rem_8rem_7rem]';
// Prévia do resumo (meia largura): só quem recebe e o total recebido.
const AGGREGATE_PREVIEW_COLS = 'grid-cols-[minmax(0,1fr)_7rem]';
const SINGLE_COLS = 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5rem_7rem_10rem]';
// Prévia do resumo: só personagem e quem recebe; papel, cota e status ficam no "ver tudo".
const SINGLE_PREVIEW_COLS = 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]';

const STATUS_COLORS = { PAID: 'text-green-400', DONATED: 'text-purple-400', PENDING: 'text-yellow-400' };

const Gold = ({ amount, className = '' }) => (
  <span className={className} title={Number(amount).toLocaleString('pt-BR')}>{formatGold(amount)}</span>
);

// Uma linha só: prova pendente tem prioridade sobre o status do pagamento.
const StatusLabel = ({ row }) => {
  if (row.proofPending) return <span className="text-sm text-red-400">Prova pendente</span>;
  if (row.compulsoryDonation) return <span className="text-sm text-orange-400">Doação compulsória</span>;
  return (
    <span className={`text-sm ${STATUS_COLORS[row.status] || 'text-gray-400'}`}>
      {STATUS_LABELS[row.status] || row.status}
    </span>
  );
};

const DistributionTable = ({ distribution, limit }) => {
  const [role, setRole] = useState('');

  if (distribution.rows.length === 0) {
    return <p className="text-gray-400 text-sm">Nenhum beneficiário neste recorte.</p>;
  }

  if (distribution.mode === 'aggregate') {
    const rows = limit ? distribution.rows.slice(0, limit) : distribution.rows;
    if (limit) {
      return (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className={`grid ${AGGREGATE_PREVIEW_COLS} gap-4 px-4 py-2 bg-white/5 text-gray-400 text-xs uppercase`}>
            <span>Beneficiário</span><span className="text-right">Recebido</span>
          </div>
          {rows.map((row) => (
            <div key={row.recipientName} className={`grid ${AGGREGATE_PREVIEW_COLS} gap-4 items-center px-4 py-3 border-t border-white/5`}>
              <span className="text-white break-words">{row.recipientName}</span>
              <Gold amount={row.received} className="text-green-400 text-right" />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <div className={`hidden sm:grid ${AGGREGATE_COLS} gap-4 px-4 py-2 bg-white/5 text-gray-400 text-xs uppercase`}>
          <span>Beneficiário</span><span>BCs</span><span className="text-right">Recebido</span>
          <span className="text-right">Doado</span><span className="text-right">Pendente</span>
        </div>
        {rows.map((row) => (
          <div key={row.recipientName} className={`grid grid-cols-2 ${AGGREGATE_COLS} gap-1 sm:gap-4 items-center px-4 py-3 border-t border-white/5`}>
            <span className="text-white col-span-2 sm:col-span-1 break-words">{row.recipientName}</span>
            <span className="text-gray-400 text-sm">{row.bcCount} BC(s)</span>
            <Gold amount={row.received} className="text-green-400 sm:text-right" />
            <span className="sm:text-right">
              <Gold amount={row.donated} className="text-purple-400" />
              {row.compulsoryDonated > 0 && (
                <span className="block text-xs text-orange-400">
                  <Gold amount={row.compulsoryDonated} /> compulsória
                </span>
              )}
            </span>
            <Gold amount={row.pending} className="text-yellow-400 sm:text-right" />
          </div>
        ))}
      </div>
    );
  }

  const filtered = filterByRole(distribution.rows, role);
  const rows = limit ? filtered.slice(0, limit) : filtered;
  const cols = limit ? SINGLE_PREVIEW_COLS : SINGLE_COLS;

  return (
    <div className="space-y-3">
      {!limit && (
        <div className="w-full sm:w-56">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">Todos os papéis</option>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </div>
      )}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <div className={`hidden sm:grid ${cols} gap-4 px-4 py-2 bg-white/5 text-gray-400 text-xs uppercase`}>
          <span>Personagem</span><span>Recebe</span>
          {!limit && (
            <>
              <span>Papel</span><span className="text-right">Cota</span><span>Status</span>
            </>
          )}
        </div>
        {rows.map((row, index) => (
          <div key={`${row.characterName}-${index}`} className={`grid grid-cols-2 ${cols} gap-1 sm:gap-4 items-center px-4 py-3 border-t border-white/5`}>
            <span className="text-white break-words">{row.characterName}</span>
            <span className="text-gray-300 break-words">{row.recipientName}</span>
            {!limit && (
              <>
                <span className="text-gray-400 text-sm">{ROLE_LABELS[row.role] || row.role}</span>
                <Gold amount={row.shareAmount} className="text-white font-semibold sm:text-right" />
                <StatusLabel row={row} />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DistributionTable;
